import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import ShiftPrintModal from '@/components/pos/ShiftPrintModal';
import { Download, FileText, Clock, Menu, Printer } from 'lucide-react';
import Sidebar from '@/components/common/Sidebar';

const formatDate = (date: Date) => {
  return date.toISOString().split('T')[0];
};

export default function RawDataPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const [activeTab, setActiveTab] = useState<'invoices' | 'shifts'>('invoices');
  const [fromDate, setFromDate] = useState(formatDate(new Date()));
  const [toDate, setToDate] = useState(formatDate(new Date()));
  const [printShiftName, setPrintShiftName] = useState<string | null>(null);

  const { data: invoices, isLoading: loadingInvoices } = useQuery({
    queryKey: ['rawInvoices', fromDate, toDate],
    queryFn: () => reportsApi.getRawInvoices(fromDate, toDate),
    enabled: activeTab === 'invoices',
  });

  const { data: shifts, isLoading: loadingShifts } = useQuery({
    queryKey: ['rawShifts', fromDate, toDate],
    queryFn: () => reportsApi.getRawShifts(fromDate, toDate),
    enabled: activeTab === 'shifts',
  });

  const downloadCSV = (data: any[], filename: string) => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const val = row[header];
          if (val === null || val === undefined) return '';
          // Wrap in quotes if it contains comma or double quotes
          const str = String(val).replace(/"/g, '""');
          return `"${str}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = () => {
    if (activeTab === 'invoices') {
      downloadCSV(invoices || [], `Invoices_Export_${formatDate(new Date())}`);
    } else {
      downloadCSV(shifts || [], `Shifts_Export_${formatDate(new Date())}`);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('rawData.title')}</h1>
                <p className="text-gray-500 mt-1">{t('rawData.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={handleExport}
              disabled={activeTab === 'invoices' ? !invoices?.length : !shifts?.length}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> {t('rawData.exportCsv')}
            </button>
          </div>

          {/* Filters & Tabs */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'invoices' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <FileText className="w-4 h-4" /> {t('rawData.invoices')}
                </button>
                <button
                  onClick={() => setActiveTab('shifts')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'shifts' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Clock className="w-4 h-4" /> {t('rawData.shifts')}
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <label className="text-gray-500">{t('common.from')}:</label>
                  <input
                    type="date"
                    value={fromDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="border border-gray-200 rounded px-2 py-1 outline-none focus:border-primary-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-500">{t('common.to')}:</label>
                  <input
                    type="date"
                    value={toDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setToDate(e.target.value)}
                    className="border border-gray-200 rounded px-2 py-1 outline-none focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  {activeTab === 'invoices' ? (
                    <tr>
                      <th className="px-6 py-3 font-medium">{t('rawData.invoiceNo')}</th>
                      <th className="px-6 py-3 font-medium">{t('common.date')}</th>
                      <th className="px-6 py-3 font-medium">{t('reports.customer')}</th>
                      <th className="px-6 py-3 font-medium">{t('rawData.cashier')}</th>
                      <th className="px-6 py-3 font-medium">{t('rawData.shift')}</th>
                      <th className="px-6 py-3 font-medium">{t('common.status')}</th>
                      <th className="px-6 py-3 font-medium text-right">{t('common.total')}</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="px-6 py-3 font-medium">{t('rawData.shiftNo')}</th>
                      <th className="px-6 py-3 font-medium">{t('rawData.start')}</th>
                      <th className="px-6 py-3 font-medium">{t('rawData.end')}</th>
                      <th className="px-6 py-3 font-medium">{t('rawData.cashier')}</th>
                      <th className="px-6 py-3 font-medium">{t('common.status')}</th>
                      <th className="px-6 py-3 font-medium text-right">{t('rawData.opening')}</th>
                      <th className="px-6 py-3 font-medium text-right">{t('rawData.closing')}</th>
                      <th className="px-6 py-3 font-medium text-right">{t('rawData.totalSales')}</th>
                      <th className="px-6 py-3 font-medium text-right">{t('common.actions')}</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activeTab === 'invoices' && invoices?.map((inv: any) => (
                    <tr key={inv.name} className="hover:bg-gray-50">
                      <td className="px-6 py-3">{inv.name}</td>
                      <td className="px-6 py-3">{inv.invoice_date}</td>
                      <td className="px-6 py-3">{inv.customer}</td>
                      <td className="px-6 py-3">{inv.cashier}</td>
                      <td className="px-6 py-3">{inv.pos_shift}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          inv.status === 'Paid' ? 'bg-green-100 text-green-700' :
                          inv.status === 'Consolidated' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right">{inv.net_total}</td>
                    </tr>
                  ))}
                  {activeTab === 'shifts' && shifts?.map((shift: any) => (
                    <tr key={shift.name} className="hover:bg-gray-50">
                      <td className="px-6 py-3">{shift.name}</td>
                      <td className="px-6 py-3">{shift.shift_start}</td>
                      <td className="px-6 py-3">{shift.shift_end || '-'}</td>
                      <td className="px-6 py-3">{shift.cashier}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          shift.status === 'Open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {shift.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right">{shift.opening_amount}</td>
                      <td className="px-6 py-3 text-right">{shift.closing_amount}</td>
                      <td className="px-6 py-3 text-right">{shift.total_sales}</td>
                      <td className="px-6 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setPrintShiftName(shift.name)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-100 rounded-lg hover:bg-primary-100"
                        >
                          <Printer className="w-3.5 h-3.5" /> {t('common.print')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {activeTab === 'invoices' && !loadingInvoices && (!invoices || invoices.length === 0) && (
                <div className="p-8 text-center text-gray-500">{t('rawData.noInvoices')}</div>
              )}
              {activeTab === 'shifts' && !loadingShifts && (!shifts || shifts.length === 0) && (
                <div className="p-8 text-center text-gray-500">{t('rawData.noShifts')}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {printShiftName && (
        <ShiftPrintModal
          shiftName={printShiftName}
          title="Print Shift Report"
          onClose={() => setPrintShiftName(null)}
        />
      )}
    </div>
  );
}
